/**
 * Brand layer — the single source of truth for the Shopify webshop's graphical profile.
 *
 * This file is the ONLY place that holds values copied from the webshop. `theme.ts` derives
 * everything from it, so re-skinning the app when the webshop changes means editing this file
 * and nothing else.
 *
 * ---------------------------------------------------------------------------------------------
 * SYNCED FROM: https://tryllepapp.no (Shopify, Dawn theme) on 2026-07-28
 *
 * To re-sync: open the webshop, and run this in the DevTools console. Every key below maps 1:1
 * to one of the CSS custom properties it prints.
 *
 *   const dump = {};
 *   for (const sheet of document.styleSheets) {
 *     try {
 *       for (const rule of sheet.cssRules) {
 *         if (rule.selectorText && /(:root|\.color-scheme-)/.test(rule.selectorText)) {
 *           const o = {};
 *           for (const p of rule.style) if (p.startsWith('--')) o[p] = rule.style.getPropertyValue(p).trim();
 *           if (Object.keys(o).length) dump[rule.selectorText] = o;
 *         }
 *       }
 *     } catch (e) {}   // cross-origin sheet, skip
 *   }
 *   copy(JSON.stringify(dump, null, 2));
 *
 * Dawn reports colors as bare RGB triplets ("2,9,18"); convert to hex when pasting here.
 * ---------------------------------------------------------------------------------------------
 */

export const brand = {
  /**
   * Dawn color schemes, kept under their Shopify names so a re-sync is a mechanical copy.
   * `base` is `:root`/.color-scheme-1, `dark` is .color-scheme-4, and `chrome` is the scheme
   * the webshop assigns to its header and footer sections.
   */
  scheme: {
    base: {
      background: '#fcfcfc',
      foreground: '#020912',
      button: '#020912',
      buttonText: '#fcfcfc',
      secondaryButton: '#fcfcfc',
      secondaryButtonText: '#020912',
      link: '#020912',
    },
    dark: {
      background: '#020912',
      foreground: '#fcfcfc',
      /** Dawn's --color-background-contrast for the dark scheme. */
      contrast: '#2b7ee8',
    },
    /**
     * Header and footer. Note the inversion: on this scheme the webshop flips buttons to a light
     * fill with near-black text, because near-black on dark navy would not read.
     */
    chrome: {
      background: '#2d3544',
      foreground: '#ffffff',
      button: '#fcfcfc',
      buttonText: '#020912',
    },
    /** Announcement bar — one step lighter than the chrome. */
    chromeAlt: {
      background: '#383f4e',
    },
    /** Soft scheme backgrounds. Dawn pairs these with `base.foreground` text, never white. */
    infoBlue: '#e4f6ff',
    infoGreen: '#dbe9e0',
    infoOrange: '#ffdfb8',
  },

  typography: {
    bodyFamily: "'Roboto Slab', serif",
    bodyWeight: 400,
    bodyWeightBold: 700,
    /** Dawn's --font-body-scale. Applied to `body`, so rem-based spacing is unaffected. */
    bodyScale: 1.05,
    headingFamily: "'Figtree', sans-serif",
    headingWeight: 500,
    /** Dawn's --font-heading-scale. */
    headingScale: 1.1905,
    // Letter-spacing is recorded in px, not rem. Dawn sets `html { font-size: 62.5% }` so its
    // rem is 10px while this app's is 16px — these are the values as actually computed on the
    // webshop, which is what we need to reproduce.
    headingLetterSpacing: '0.75px',
    buttonLetterSpacing: '1.05px',
    navLetterSpacing: '0.63px',
    fontImportUrl:
      'https://fonts.googleapis.com/css2?family=Figtree:wght@400;500;600;700&family=Roboto+Slab:wght@400;500;600;700&display=swap',
  },

  /** Corner radii. The webshop is square everywhere except badges and variant pills. */
  shape: {
    button: '0px',
    input: '0px',
    card: '0px',
    media: '0px',
    badge: '40px',
    pill: '40px',
  },

  border: {
    button: '1px',
    input: '1px',
    card: '2px',
    popup: '2px',
    /** Dawn's --inputs-border-opacity. */
    inputOpacity: 0.55,
  },

  /** Every shadow opacity on the webshop is 0.0 — the design is flat and relies on borders. */
  elevation: {
    card: 'none',
    cardHover: 'none',
    popup: 'none',
  },

  /**
   * Control metrics, in px for the same reason as letter-spacing above.
   *
   * Deliberately NOT adopted: the webshop's button min-height (45px) and min-width (120px). The
   * buylist puts buttons inside dense table rows where those would force a relayout, and this
   * change is meant to be cosmetic only. Squareness, color and letter-spacing carry the brand.
   */
  metrics: {
    buttonPaddingX: '31.5px',
    inputHeight: '45px',
  },

  /**
   * Header, measured off the webshop. The logo is a fixed 100px wide at every viewport (the
   * theme's logo_width setting) — it does not scale down on mobile — and its 288x340 source
   * gives 118px of height. The header has no fixed height: it is the logo plus the paddings,
   * which is why the resulting bar is 173.75px on desktop and 153.75px below 990px.
   */
  header: {
    logoWidth: '100px',
    /** .header__heading-link padding: 0.75rem at the webshop's 10.5px root. */
    logoPadding: '7.875px',
    paddingDesktop: '20px 52.5px',
    paddingMobile: '10px 31.5px',
    /** The webshop switches header padding at this width. */
    breakpoint: '990px',
    /**
     * Resulting bar height: logo height (100px wide at the 288x340 aspect = 118.06px) plus the
     * logo padding and the header padding both doubled, plus the 1px divider border. Exposed as
     * --tp-header-height so sticky elements below the header clear it instead of hiding under it.
     */
    heightMobile: '154.81px',
    heightDesktop: '174.81px',

    /**
     * DELIBERATE DIVERGENCE from the webshop. The webshop keeps the logo at 100px on every
     * viewport, which makes its header 153.81px tall — on a phone that is ~19% of the screen,
     * and unlike a storefront this app is a dense, scroll-heavy interface with a sticky header.
     *
     * So below the app's tablet breakpoint (where the nav collapses to the hamburger) the logo
     * shrinks and the bar drops to 102.86px. Change `logoWidthSmall` to taste; `heightSmall`
     * must be recomputed alongside it as logoWidthSmall * 340/288 + 15.75 + 20 + 1.
     */
    logoWidthSmall: '56px',
    heightSmall: '102.86px',
  },

  layout: {
    /** Dawn's --page-width, 140rem. */
    pageWidth: '1400px',
    sectionSpacing: '20px',
    gridGap: '24px',
    gridGapMobile: '12px',
  },

  motion: {
    short: '0.1s',
    default: '0.2s',
    medium: '0.3s',
    long: '0.5s',
    ease: 'cubic-bezier(0, 0, .3, 1)',
  },

  assets: {
    logo: '/logo-tryllepapp.png',
    logoAlt: 'Tryllepapp',
  },
} as const;

export type Brand = typeof brand;

/**
 * The brand values as CSS custom properties, injected once into `:root` by `GlobalStyle`.
 *
 * This exists so plain CSS (`index.css`), prerendered markup and any future non-styled-components
 * code can read the same values instead of hardcoding copies that silently drift.
 */
export const brandCssVariables = `
  --tp-color-background: ${brand.scheme.base.background};
  --tp-color-foreground: ${brand.scheme.base.foreground};
  --tp-color-button: ${brand.scheme.base.button};
  --tp-color-button-text: ${brand.scheme.base.buttonText};
  --tp-color-secondary-button: ${brand.scheme.base.secondaryButton};
  --tp-color-secondary-button-text: ${brand.scheme.base.secondaryButtonText};
  --tp-color-link: ${brand.scheme.base.link};
  --tp-color-dark-background: ${brand.scheme.dark.background};
  --tp-color-dark-foreground: ${brand.scheme.dark.foreground};
  --tp-color-contrast: ${brand.scheme.dark.contrast};
  --tp-color-chrome: ${brand.scheme.chrome.background};
  --tp-color-chrome-foreground: ${brand.scheme.chrome.foreground};
  --tp-color-chrome-button: ${brand.scheme.chrome.button};
  --tp-color-chrome-button-text: ${brand.scheme.chrome.buttonText};
  --tp-color-chrome-alt: ${brand.scheme.chromeAlt.background};
  --tp-color-info-blue: ${brand.scheme.infoBlue};
  --tp-color-info-green: ${brand.scheme.infoGreen};
  --tp-color-info-orange: ${brand.scheme.infoOrange};

  --tp-font-body-family: ${brand.typography.bodyFamily};
  --tp-font-body-weight: ${brand.typography.bodyWeight};
  --tp-font-body-weight-bold: ${brand.typography.bodyWeightBold};
  --tp-font-body-scale: ${brand.typography.bodyScale};
  --tp-font-heading-family: ${brand.typography.headingFamily};
  --tp-font-heading-weight: ${brand.typography.headingWeight};
  --tp-font-heading-scale: ${brand.typography.headingScale};
  --tp-font-heading-letter-spacing: ${brand.typography.headingLetterSpacing};
  --tp-font-button-letter-spacing: ${brand.typography.buttonLetterSpacing};
  --tp-font-nav-letter-spacing: ${brand.typography.navLetterSpacing};

  --tp-radius-button: ${brand.shape.button};
  --tp-radius-input: ${brand.shape.input};
  --tp-radius-card: ${brand.shape.card};
  --tp-radius-media: ${brand.shape.media};
  --tp-radius-badge: ${brand.shape.badge};
  --tp-radius-pill: ${brand.shape.pill};

  --tp-border-button: ${brand.border.button};
  --tp-border-input: ${brand.border.input};
  --tp-border-card: ${brand.border.card};
  --tp-border-popup: ${brand.border.popup};

  --tp-shadow-card: ${brand.elevation.card};
  --tp-shadow-card-hover: ${brand.elevation.cardHover};
  --tp-shadow-popup: ${brand.elevation.popup};

  --tp-button-padding-x: ${brand.metrics.buttonPaddingX};
  --tp-input-height: ${brand.metrics.inputHeight};

  --tp-header-height: ${brand.header.heightMobile};
  --tp-header-logo-width: ${brand.header.logoWidth};
  --tp-header-logo-padding: ${brand.header.logoPadding};
  --tp-header-padding-desktop: ${brand.header.paddingDesktop};
  --tp-header-padding-mobile: ${brand.header.paddingMobile};

  --tp-page-width: ${brand.layout.pageWidth};
  --tp-section-spacing: ${brand.layout.sectionSpacing};
  --tp-grid-gap: ${brand.layout.gridGap};
  --tp-grid-gap-mobile: ${brand.layout.gridGapMobile};

  --tp-duration-short: ${brand.motion.short};
  --tp-duration-default: ${brand.motion.default};
  --tp-duration-medium: ${brand.motion.medium};
  --tp-duration-long: ${brand.motion.long};
  --tp-ease: ${brand.motion.ease};
`;
